import { useEffect, useMemo, useState } from "react";
import { Route, Routes, useNavigate } from "react-router-dom";

import Header from "./components/Header";
import Footer from "./components/Footer";
import Home from "./pages/Home";
import About from "./pages/About";
import Contact from "./pages/Contact";
import Placements from "./pages/Placements";
import Login from "./pages/Login";
import Register from "./pages/Register";
import AdminDashboard from "./dashboard/AdminDashboard";
import TrainerDashboard from "./dashboard/TrainerDashboard";
import TraineeDashboard from "./dashboard/TraineeDashboard";

import api from "./services/api";

const storageKey = "tms-session";

const capitalize = (text) => text ? text.charAt(0).toUpperCase() + text.slice(1) : "";

const normalizeUser = (user) => ({
  id: Number(user.id),
  name: user.name,
  email: user.email,
  phone: user.phone,
  role: capitalize(user.role),
  assignedTrainer: user.assigned_trainer_id ? Number(user.assigned_trainer_id) : null,
  profilePhoto: user.profile_photo || "",
});

const normalizePlacement = (placement) => ({
  id: Number(placement.id),
  company: placement.company_name || placement.company,
  role: placement.job_role || placement.role,
  location: placement.location,
  package: placement.package,
  description: placement.description,
});

const statusToUi = (status) => {
  const map = {
    pending: "Pending",
    in_progress: "In Progress",
    completed: "Completed",
    Pending: "Pending",
    "In Progress": "In Progress",
    Completed: "Completed",
  };
  return map[status] || "Pending";
};

const statusToApi = (status) => {
  const map = {
    Pending: "pending",
    "In Progress": "in_progress",
    Completed: "completed",
  };
  return map[status] || "pending";
};

const normalizeTask = (task) => ({
  id: Number(task.id),
  title: task.title,
  description: task.description,
  trainerId: Number(task.trainer_id ?? task.trainerId ?? 0),
  traineeId: Number(task.trainee_id ?? task.traineeId ?? 0),
  status: statusToUi(task.status),
});

const normalizeAttendance = (record) => ({
  id: Number(record.id),
  userId: Number(record.user_id ?? record.userId ?? 0),
  role: capitalize(record.role),
  date: record.attendance_date || record.date,
  status: record.status,
});

const normalizeNotification = (item) => ({
  id: Number(item.id),
  message: item.message || item.title,
  role: "Trainee",
  date: item.created_at || item.date || new Date().toISOString(),
});

const App = () => {
  const [users, setUsers] = useState([]);
  const [placements, setPlacements] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [attendance, setAttendance] = useState([]);
  const [applications, setApplications] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [session, setSession] = useState(() => {
    const stored = localStorage.getItem(storageKey);
    if (!stored) return null;
    try {
      return JSON.parse(stored);
    } catch {
      return null;
    }
  });
  const [sharedMessage, setSharedMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const trainers = useMemo(() => users.filter((user) => user.role === "Trainer"), [users]);

  useEffect(() => {
    if (session) {
      localStorage.setItem(storageKey, JSON.stringify(session));
    } else {
      localStorage.removeItem(storageKey);
    }
  }, [session]);

  useEffect(() => {
    if (!sharedMessage) return undefined;
    const timeout = setTimeout(() => setSharedMessage(""), 4000);
    return () => clearTimeout(timeout);
  }, [sharedMessage]);

  const bootstrapData = async (role, currentUser) => {
    try {
      if (role === "Admin") {
        const list = await api.adminViewUsers();
        const flattened = [
          ...list.admin.map(normalizeUser),
          ...list.trainer.map(normalizeUser),
          ...list.trainee.map(normalizeUser),
        ];
        setUsers(flattened);
        const attendanceData = await api.adminViewAttendance();
        setAttendance(attendanceData.map(normalizeAttendance));
        const placementData = await api.placements();
        setPlacements(placementData.map(normalizePlacement));
        const applicants = await api.adminPlacementApplicants();
        setApplications(applicants);
      } else if (role === "Trainer") {
        const trainees = await api.assignedTrainees();
        const mappedTrainees = trainees.map(normalizeUser);
        setUsers([...(currentUser ? [currentUser] : []), ...mappedTrainees]);
        const tasksData = await api.traineeTasks();
        setTasks(tasksData.map(normalizeTask));
        const attendanceData = await api.trainerAttendance();
        setAttendance(attendanceData.map(normalizeAttendance));
        const placementData = await api.placements();
        setPlacements(placementData.map(normalizePlacement));
      } else if (role === "Trainee") {
        const placementData = await api.placements();
        setPlacements(placementData.map(normalizePlacement));
        const notes = await api.notifications();
        setNotifications(notes.items.map(normalizeNotification));
        const tasksData = await api.myTasks();
        setTasks(tasksData.map(normalizeTask));
        const myApps = await api.myApplications();
        setApplications(myApps);
        const today = new Date().toISOString().split("T")[0];
        try {
          await api.markAttendance();
          setAttendance([{ id: Date.now(), userId: currentUser?.id, role, date: today, status: "Present" }]);
        } catch {
          // ignore if already marked
        }
      }
    } catch (err) {
      console.error(err);
      setSharedMessage(err.message);
    }
  };

  const handleLogin = async ({ email, password }) => {
    setLoading(true);
    try {
      const data = await api.login(email, password);
      const mappedUser = normalizeUser(data.user);
      setSession(mappedUser);
      await bootstrapData(mappedUser.role, mappedUser);
      return mappedUser;
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await api.logout().catch(() => {});
    setSession(null);
    setUsers([]);
    setTasks([]);
    setNotifications([]);
    setPlacements([]);
    setAttendance([]);
    navigate("/");
  };

  const handleRegister = async (payload) => {
    setLoading(true);
    try {
      await api.register({
        name: payload.name,
        email: payload.email,
        phone: payload.phone,
        password: payload.password,
        confirm_password: payload.confirm_password || payload.password,
      });
      setSharedMessage("Registration successful. Please login.");
    } finally {
      setLoading(false);
    }
  };

  const handleAddUser = async (user) => {
    const body = {
      name: user.name,
      email: user.email,
      phone: user.phone,
      password: user.password,
      role: user.role,
      assigned_trainer_id: user.assignedTrainer || "",
      profile_photo: user.profilePhoto || "",
    };
    if (user.role === "Trainer") {
      await api.adminCreateTrainer(body);
    } else {
      await api.adminCreateTrainee(body);
    }
    const refreshed = await api.adminViewUsers();
    const flattened = [
      ...refreshed.admin.map(normalizeUser),
      ...refreshed.trainer.map(normalizeUser),
      ...refreshed.trainee.map(normalizeUser),
    ];
    setUsers(flattened);
  };

  const handleAssignTrainee = async (traineeId, trainerId) => {
    await api.adminAssignTrainee({ trainee_id: traineeId, trainer_id: trainerId });
    const refreshed = await api.adminViewUsers();
    const flattened = [
      ...refreshed.admin.map(normalizeUser),
      ...refreshed.trainer.map(normalizeUser),
      ...refreshed.trainee.map(normalizeUser),
    ];
    setUsers(flattened);
  };

  const refreshPlacements = async () => {
    const data = await api.placements();
    setPlacements(data.map(normalizePlacement));
  };

  const handleAddOrUpdatePlacement = async (payload) => {
    if (payload.id) {
      await api.adminUpdatePlacement({
        placement_id: payload.id,
        company_name: payload.company,
        job_role: payload.role,
        location: payload.location,
        package: payload.package,
        description: payload.description,
        user_id: session?.id,
      });
    } else {
      await api.adminCreatePlacement({
        company_name: payload.company,
        job_role: payload.role,
        location: payload.location,
        package: payload.package,
        description: payload.description,
        user_id: session?.id,
      });
    }
    await refreshPlacements();
    const applicants = await api.adminPlacementApplicants().catch(() => null);
    if (applicants) setApplications(applicants);
    setSharedMessage(payload.id ? "Placement updated." : "Placement saved and trainees notified.");
  };

  const handleDeletePlacement = async (placementId) => {
    await api.adminDeletePlacement(placementId);
    await refreshPlacements();
  };

  const refreshUsers = async () => {
    const refreshed = await api.adminViewUsers();
    const flattened = [
      ...refreshed.admin.map(normalizeUser),
      ...refreshed.trainer.map(normalizeUser),
      ...refreshed.trainee.map(normalizeUser),
    ];
    setUsers(flattened);
  };

  const handleUpdateUser = async (user) => {
    await api.adminUpdateUser({
      user_id: user.id,
      name: user.name,
      email: user.email,
      phone: user.phone,
      role: user.role.toLowerCase(),
      assigned_trainer_id: user.assignedTrainer || "",
      profile_photo: user.profilePhoto || "",
      password: user.password || "",
    });
    await refreshUsers();
  };

  const handleDeleteUser = async (userId) => {
    await api.adminDeleteUser(userId);
    await refreshUsers();
  };

  const handleCreateTask = async (task) => {
    await api.createTask({
      title: task.title,
      description: task.description,
      trainee_id: task.traineeId,
      user_id: session?.id,
    });
    const updated = await api.traineeTasks();
    setTasks(updated.map(normalizeTask));
  };

  const handleUpdateTaskStatus = async (taskId, statusUi) => {
    const status = statusToApi(statusUi);
    await api.updateTaskStatus(taskId, status);
    const updated = await api.myTasks();
    setTasks(updated.map(normalizeTask));
  };

  const handleApplyPlacement = (placementId) => {
    const placement = placements.find((item) => item.id === placementId);
    if (!placement) return;
    setSharedMessage(`Applied for ${placement.role} at ${placement.company}`);
  };

  // Bootstrap data if session already stored (page refresh)
  useEffect(() => {
    if (session?.role) {
      bootstrapData(session.role, session);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="app-shell">
      <Header session={session} onLogout={handleLogout} />
      {sharedMessage && <div className="shared-callout">{sharedMessage}</div>}
      <main>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/about" element={<About />} />
          <Route path="/contact" element={<Contact />} />
          <Route
            path="/placements"
            element={
              <Placements
                placements={placements}
                onAddPlacement={handleAddOrUpdatePlacement}
                session={session}
              />
            }
          />
          <Route path="/login" element={<Login onLogin={handleLogin} />} />
          <Route path="/register" element={<Register onRegister={handleRegister} />} />
          <Route
            path="/admin"
            element={
              <AdminDashboard
                session={session}
                users={users}
                attendance={attendance}
                applications={applications}
                placements={placements}
                onAddUser={handleAddUser}
                onAssignTrainee={handleAssignTrainee}
                onSavePlacement={handleAddOrUpdatePlacement}
                onDeletePlacement={handleDeletePlacement}
              />
            }
          />
          <Route
            path="/trainer"
            element={
              <TrainerDashboard
                session={session}
                users={users}
                tasks={tasks}
                attendance={attendance}
                onCreateTask={handleCreateTask}
              />
            }
          />
          <Route
            path="/trainee"
            element={
              <TraineeDashboard
                session={session}
                tasks={tasks}
                placements={placements}
                notifications={notifications}
                attendance={attendance}
                onUpdateTaskStatus={handleUpdateTaskStatus}
                onApply={handleApplyPlacement}
              />
            }
          />
          <Route path="*" element={<Home />} />
        </Routes>
      </main>
      <Footer />
    </div>
  );
};

export default App;
